import {
  LinearMipmapLinearFilter,
  LinearMipmapNearestFilter,
  Material,
  Mesh,
  Object3D,
  Texture,
} from "three";

// DPR 상향 없이 경사면(모발·도복 주름·외곽선)의 축소 샘플링만 개선하는 샘플 수.
// ai-avatar-vercel-runtime에서 fill-rate·발열 추가 없이 검증된 값 (2026-08-31).
export const AVATAR_TEXTURE_ANISOTROPY = 4;

export interface AvatarTextureSamplingReport {
  textureCount: number;
  trilinearUpgrades: number;
  anisotropy: number;
}

/**
 * 모델 트리의 모든 메시 재질에서 텍스처를 수집한다.
 */
export function collectMeshTextures(root: Object3D): Texture[] {
  const textures: Texture[] = [];
  root.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    const materials: Material[] = Array.isArray(child.material)
      ? child.material
      : child.material
        ? [child.material]
        : [];
    for (const material of materials) {
      for (const value of Object.values(material)) {
        if ((value as Texture | null)?.isTexture) {
          textures.push(value as Texture);
        }
      }
    }
  });
  return textures;
}

/**
 * 저작된 재질·텍스처 이미지는 그대로 두고 축소 샘플링만 개선한다.
 * 로더가 mip 체인을 이미 만들어 두므로 네트워크 전송량·상주 GPU 메모리는 변하지 않는다.
 * 제작자가 더 강하게 설정한 값(트라이리니어, 높은 anisotropy)은 낮추지 않는다.
 */
export function improveAvatarTextureSampling(
  textures: readonly Texture[],
  supportedMaxAnisotropy: number,
): AvatarTextureSamplingReport {
  const anisotropy = Math.max(1, Math.min(
    AVATAR_TEXTURE_ANISOTROPY,
    Number.isFinite(supportedMaxAnisotropy) ? supportedMaxAnisotropy : 1,
  ));
  const unique = [...new Set(textures)];
  let trilinearUpgrades = 0;
  for (const texture of unique) {
    const mipmapped = texture.minFilter === LinearMipmapNearestFilter
      || texture.minFilter === LinearMipmapLinearFilter;
    if (!mipmapped) continue;
    let changed = false;
    if (texture.minFilter === LinearMipmapNearestFilter) {
      texture.minFilter = LinearMipmapLinearFilter;
      trilinearUpgrades += 1;
      changed = true;
    }
    if (texture.anisotropy < anisotropy) {
      texture.anisotropy = anisotropy;
      changed = true;
    }
    if (changed) texture.needsUpdate = true;
  }
  return { textureCount: unique.length, trilinearUpgrades, anisotropy };
}
